import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Google Analytics page views (F5). index.html loads gtag with send_page_view:false, so
// nothing goes out by itself; PageViews below sends one page_view per page, and:
//  - never on password and sign-in-code links (their address carries a secret);
//  - never while a student is signed in (staff and signed-out visitors are fine);
//  - any part of the address that looks like a token is blanked out, and the query
//    string and hash are left off.

const GA_ID = "G-WBSVP6V9YQ";

// Routes whose address is a secret: nothing is sent at all
const SECRET_ROUTES = [/^\/set-password(\/|$)/, /^\/reset-password(\/|$)/, /sign-in-code/i];

// A path part that is an id or a token rather than a page name
const looksLikeToken = (part) =>
  /^\d+$/.test(part) ||
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(part) ||
  (part.length >= 16 && /\d/.test(part)) ||
  /[:=]/.test(part);

/** The path to report, or null when this page must not be reported. */
export const pageViewPath = (pathname = "/") => {
  const path = String(pathname || "/");
  if (SECRET_ROUTES.some((route) => route.test(path))) return null;
  const parts = path.split("/").map((part) => (part && looksLikeToken(part) ? ":id" : part));
  return parts.join("/") || "/";
};

/** Who is looking: "guest", "staff", or "student" (never reported). Null while unknown. */
export const viewerKind = (user, hasToken) => {
  if (!hasToken) return "guest";
  if (user && (user.is_staff || user.is_superuser)) return "staff";
  if (user && (user.id || user.email || user.role)) return "student";
  return null; // signed in, but /auth/user/ hasn't answered yet
};

// GA's own off switch: while it's set, gtag sends nothing at all (page views, and the
// engagement/scroll events that would otherwise carry the full address too).
const DISABLE_FLAG = `ga-disable-${GA_ID}`;
export const setAnalyticsOff = (off) => {
  window[DISABLE_FLAG] = Boolean(off);
};

export const sendPageView = (path, kind) => {
  if (typeof window.gtag !== "function") return;
  // Later automatic events report this cleaned address, not the real one
  window.gtag("set", { page_location: `${window.location.origin}${path}`, page_path: path });
  window.gtag("event", "page_view", {
    send_to: GA_ID,
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
    tenant: window.location.hostname,
    userType: kind === "staff" ? "staff" : "guest",
  });
};

const hasToken = () => {
  try {
    return Boolean(localStorage.getItem("authToken"));
  } catch {
    return false;
  }
};

/** Mounted once inside the Router (App.js). */
export const PageViews = ({ user }) => {
  const { pathname } = useLocation();
  const lastSent = useRef(null);
  useEffect(() => {
    const path = pageViewPath(pathname);
    const kind = viewerKind(user, hasToken());
    const allowed = Boolean(path) && kind !== null && kind !== "student";
    setAnalyticsOff(!allowed);
    if (!allowed) {
      lastSent.current = null;
      return;
    }
    if (lastSent.current === path) return;
    lastSent.current = path;
    sendPageView(path, kind);
  }, [pathname, user]);
  return null;
};
